// T044 — Wallet listing table. PrimeReact DataTable with cursor-based "Load
// more", sortable columns that translate to API sort enum values, and row-click
// navigation into the detail page.
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableSortEvent,
  DataTableSortMeta,
} from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { formatEur } from "../../../utils/eur";
import { formatDate } from "../../../utils/formats";
import type {
  WalletListItem,
  WalletListQuery,
  WalletListSort,
} from "../../../types/wallet";

interface WalletListTableProps {
  items: WalletListItem[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  query: WalletListQuery;
  searchInput: string;
  onLoadMore: () => void;
  onRetry: () => void;
  onSortChange: (sort: WalletListSort) => void;
}

// Map between API sort enum and PrimeReact's { sortField, sortOrder } shape.
const SORT_FIELD: Record<WalletListSort, string> = {
  spendable_balance_desc: "spendable_balance",
  spendable_balance_asc: "spendable_balance",
  last_activity_desc: "last_activity_at",
  last_activity_asc: "last_activity_at",
  created_at_desc: "created_at",
  created_at_asc: "created_at",
};

const sortAscending = (sort: WalletListSort) => sort.endsWith("_asc");

const sortToMeta = (sort: WalletListSort | undefined): DataTableSortMeta => ({
  field: SORT_FIELD[sort ?? "last_activity_desc"],
  order: sortAscending(sort ?? "last_activity_desc") ? 1 : -1,
});

const fieldToSort = (
  field: string,
  ascending: boolean,
): WalletListSort | null => {
  switch (field) {
    case "spendable_balance":
      return ascending ? "spendable_balance_asc" : "spendable_balance_desc";
    case "last_activity_at":
      return ascending ? "last_activity_asc" : "last_activity_desc";
    case "created_at":
      return ascending ? "created_at_asc" : "created_at_desc";
    default:
      return null;
  }
};

const WalletListTable = ({
  items,
  loading,
  hasMore,
  error,
  query,
  searchInput,
  onLoadMore,
  onRetry,
  onSortChange,
}: WalletListTableProps) => {
  const navigate = useNavigate();

  const handleSort = (event: DataTableSortEvent) => {
    if (!event.sortField) return;
    const next = fieldToSort(String(event.sortField), event.sortOrder === 1);
    if (next && next !== query.sort) onSortChange(next);
  };

  // T047 — distinct empty-state copy per cause.
  const emptyMessage = (() => {
    if (loading) return "Loading wallets…";
    if (error) return "—";
    if (searchInput.trim()) {
      return `No wallets match "${searchInput.trim()}". v1 search matches the emaid only.`;
    }
    if (query.balance === "non_zero") {
      return "No wallets with a non-zero balance.";
    }
    if (query.balance === "zero") {
      return "No wallets at zero balance.";
    }
    return "No wallets exist yet.";
  })();

  if (error) {
    return (
      <Card>
        <Message severity="error" text={error} className="w-full mb-3" />
        <Button label="Retry" icon="pi pi-refresh" onClick={onRetry} />
      </Card>
    );
  }

  return (
    <Card>
      <DataTable
        value={items}
        loading={loading}
        size="small"
        emptyMessage={emptyMessage}
        sortMode="single"
        sortField={sortToMeta(query.sort).field}
        sortOrder={sortToMeta(query.sort).order}
        onSort={handleSort}
        selectionMode="single"
        onRowClick={(event) => {
          const row = event.data as WalletListItem;
          if (row?.account_id) {
            navigate(`/wallet-admin/${encodeURIComponent(row.account_id)}`);
          }
        }}
        rowClassName={() => "cursor-pointer"}
      >
        <Column field="account_id" header="Account (emaid)" />
        <Column
          field="spendable_balance"
          header="Spendable"
          sortable
          body={(row: WalletListItem) => formatEur(row.spendable_balance)}
        />
        <Column
          header="Lifetime earned"
          body={(row: WalletListItem) => formatEur(row.lifetime_earned)}
        />
        <Column
          header="Lifetime spent"
          body={(row: WalletListItem) => formatEur(row.lifetime_spent)}
        />
        <Column
          field="last_activity_at"
          header="Last activity"
          sortable
          body={(row: WalletListItem) => formatDate(row.last_activity_at)}
        />
        <Column
          field="created_at"
          header="Created"
          sortable
          body={(row: WalletListItem) => formatDate(row.created_at)}
        />
        <Column
          header="Auto-spend"
          body={(row: WalletListItem) => (
            <Tag
              value={row.wallet_toggle_on ? "ON" : "OFF"}
              severity={row.wallet_toggle_on ? "success" : undefined}
              className="text-xs"
            />
          )}
        />
      </DataTable>

      {hasMore && (
        <div className="flex justify-content-center mt-3">
          <Button
            label="Load more"
            icon="pi pi-chevron-down"
            severity="secondary"
            text
            loading={loading}
            onClick={onLoadMore}
          />
        </div>
      )}
    </Card>
  );
};

export default WalletListTable;
